/**
 * O histórico de cargas recebidas de uma conexão de folha.
 *
 * É o que responde ao RH que jura ter mandado a demissão: chegou, quando, com quantas linhas, e
 * o que aconteceu com cada uma. A coluna que importa numa integração saudável é "sem mudança" —
 * sincronização diária é quase toda repetição, e uma lista só de "atualizados" é sinal de que
 * algo está sendo reescrito todo dia sem motivo.
 *
 * O resumo de erro chega do backend já com o CPF mascarado. Esta tela não mascara nada, e não
 * deve passar a mascarar: fazer isso aqui daria a impressão de que o dado completo trafega.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { DataTable } from '../../../../components/common';
import { payrollIntegrationService } from '../../../../services/payrollIntegrationService';
import { PayrollConnection, PayrollSyncRun } from '../../../../types/payrollIntegration';

interface PayrollSyncRunsDialogProps {
  open: boolean;
  companyId: string;
  connection: PayrollConnection | null;
  onClose: () => void;
}

const LINHAS_POR_PAGINA = 10;

function formatarMomento(valor: string | null): string {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatarDuracao(inicio: string, fim: string | null): string {
  if (!fim) return 'em andamento';
  const ms = new Date(fim).getTime() - new Date(inicio).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

const PayrollSyncRunsDialog: React.FC<PayrollSyncRunsDialogProps> = ({
  open,
  companyId,
  connection,
  onClose,
}) => {
  const [runs, setRuns] = useState<PayrollSyncRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandida, setExpandida] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    if (!connection) return;
    setLoading(true);
    setError(null);
    try {
      const resultado = await payrollIntegrationService.syncRuns(
        companyId,
        connection.id,
        page + 1,
        LINHAS_POR_PAGINA
      );
      setRuns(resultado.runs);
      setTotal(resultado.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o histórico');
    } finally {
      setLoading(false);
    }
  }, [companyId, connection, page]);

  useEffect(() => {
    if (open) void carregar();
  }, [open, carregar]);

  useEffect(() => {
    if (!open) {
      setPage(0);
      setExpandida(null);
    }
  }, [open]);

  const colunas = [
    {
      id: 'startedAt',
      label: 'Recebida em',
      width: '22%',
      render: (row: PayrollSyncRun) => (
        <Stack spacing={0.25}>
          <Typography variant="body2">{formatarMomento(row.startedAt)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatarDuracao(row.startedAt, row.finishedAt)}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'received',
      label: 'Recebidas',
      width: '12%',
      render: (row: PayrollSyncRun) => <Typography variant="body2">{row.received}</Typography>,
    },
    {
      id: 'created',
      label: 'Criados',
      width: '12%',
      render: (row: PayrollSyncRun) => <Typography variant="body2">{row.created}</Typography>,
    },
    {
      id: 'updated',
      label: 'Atualizados',
      width: '12%',
      render: (row: PayrollSyncRun) => <Typography variant="body2">{row.updated}</Typography>,
    },
    {
      id: 'unchanged',
      label: 'Sem mudança',
      width: '14%',
      render: (row: PayrollSyncRun) => (
        <Typography variant="body2" color="text.secondary">
          {row.unchanged}
        </Typography>
      ),
    },
    {
      id: 'rejected',
      label: 'Recusadas',
      width: '28%',
      render: (row: PayrollSyncRun) => {
        if (row.rejected === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              nenhuma
            </Typography>
          );
        }

        const aberta = expandida === row.id;
        return (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip label={row.rejected} size="small" color="warning" />
              <IconButton
                size="small"
                onClick={() => setExpandida(aberta ? null : row.id)}
                aria-label={aberta ? 'Ocultar motivos' : 'Ver motivos'}
              >
                {aberta ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={aberta}>
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {row.errorSummary ?? 'Sem detalhes registrados.'}
              </Typography>
            </Collapse>
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Sincronizações recebidas</Typography>
            {connection && (
              <Typography variant="caption" color="text.secondary">
                {connection.name} · {connection.keyPrefix}…
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DataTable
          columns={colunas}
          rows={runs}
          getRowId={(row) => row.id}
          loading={loading}
          emptyState={{
            title: 'Nenhuma sincronização recebida',
            description:
              'O sistema de folha ainda não enviou nenhuma carga com esta chave. Assim que ' +
              'enviar, cada envio aparece aqui com o que foi criado, atualizado e recusado.',
          }}
          pagination={{
            page,
            rowsPerPage: LINHAS_POR_PAGINA,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: () => undefined,
            rowsPerPageOptions: [LINHAS_POR_PAGINA],
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollSyncRunsDialog;
